import { useEffect, useMemo, useRef, useState } from "react";
import {
  Circle,
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMap,
  useMapEvents,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet/dist/leaflet.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";
import L from "leaflet";
import { useNavigate } from "react-router";
import { AimOutlined } from "@ant-design/icons";

import mapMarker from "@/assets/images/map_marker.svg";
import mapMarkerSelected from "@/assets/images/map_marker_selected.svg";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { useScopeParams } from "@/hooks/useScopeParams";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import { reverseGeocode } from "@/services/geocodingService";
import { buildGeoProfileUrl } from "../utils/geoNavigation";
import {
  DEFAULT_GEO_DISPLAY_UNIT,
  DEFAULT_RADIUS_MILES,
  createGeoFilter,
  displayUnitToKm,
} from "@/components/UI/TableToolbar/Filtering/geoFilterUtil";
import RadiusEditorPanel from "./RadiusEditorPanel";
import type { GeoFilterCondition } from "@/components/UI/TableToolbar/Filtering/filterTypes";
import type { SiteDirectoryRow } from "../types";

const BRAND_COLOR = "#2B4752";
const RADIUS_CIRCLE_COLOR = "#0C98D0";

const siteIcon = new L.Icon({
  iconUrl: mapMarker,
  iconRetinaUrl: mapMarker,
  iconAnchor: [15, 45],
  iconSize: [30, 50],
});

const centerPinIcon = new L.Icon({
  iconUrl: mapMarkerSelected,
  iconRetinaUrl: mapMarkerSelected,
  iconAnchor: [15, 45],
  iconSize: [30, 50],
});

const createClusterIcon = (cluster: any) =>
  L.divIcon({
    html: `<span class="bg-[#2B4752] w-[36px] h-[36px] rounded-full flex items-center justify-center border-2 border-white font-bold text-white text-[13px] shadow-md">${cluster.getChildCount()}</span>`,
    className: "customMarker",
    iconSize: L.point(36, 36, true),
  });

function FitBoundsOnce({ sites }: { sites: SiteDirectoryRow[] }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || sites.length === 0) return;
    fitted.current = true;
    const bounds = L.latLngBounds(
      sites.map((s) => [s.latitude!, s.longitude!] as [number, number]),
    );
    map.fitBounds(bounds, { padding: [48, 48], animate: false });
  }, [map, sites]);
  return null;
}

function PlacementClickHandler({
  active,
  onPlace,
}: {
  active: boolean;
  onPlace: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click: (e) => {
      if (active) onPlace(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

type ClickedGeo = {
  lat: number;
  lng: number;
  country: string;
  regions: string[];
  siteCount: number;
  totalEnrolled: number;
};

/**
 * Idle-mode (non-placement) background clicks reverse-geocode the point and
 * open the country/region options popup — restores the pre-radius-tool
 * behavior so both interactions coexist on one map.
 */
function CountryClickHandler({
  active,
  sites,
  onLoading,
  onGeoClick,
}: {
  active: boolean;
  sites: SiteDirectoryRow[];
  onLoading: (v: boolean) => void;
  onGeoClick: (geo: ClickedGeo) => void;
}) {
  // Match clicks on the ISO alpha-2 code the geocoder returns — stable across
  // naming variants ("Czechia"/"Czech Republic"), with country-name fallback
  // for rows the backend could not normalize to a code.
  const sitesByCode = useMemo(() => {
    const byCode = new Map<string, SiteDirectoryRow[]>();
    for (const site of sites) {
      const code = site.country_code?.toUpperCase();
      if (!code) continue;
      byCode.set(code, [...(byCode.get(code) ?? []), site]);
    }
    return byCode;
  }, [sites]);

  useMapEvents({
    click: async (e) => {
      if (!active) return;
      onLoading(true);
      try {
        const data = await reverseGeocode(e.latlng.lat, e.latlng.lng);
        const clickedCode = data?.address?.country_code?.toUpperCase();
        const clickedName = data?.address?.country;
        const countrySites =
          (clickedCode && sitesByCode.get(clickedCode)) ||
          (clickedName ? sites.filter((s) => s.country === clickedName) : []);
        if (!countrySites.length) return;

        const regions = [
          ...new Set(
            countrySites.map((s) => s.region).filter((r): r is string => !!r),
          ),
        ];
        onGeoClick({
          lat: e.latlng.lat,
          lng: e.latlng.lng,
          country: countrySites[0].country ?? clickedName ?? "",
          regions,
          siteCount: countrySites.length,
          totalEnrolled: countrySites.reduce(
            (sum, s) => sum + (s.total_enrolled ?? 0),
            0,
          ),
        });
      } catch {
        // network failure — ignore
      } finally {
        onLoading(false);
      }
    },
  });
  return null;
}

type SiteDirectoryMapProps = {
  sites: SiteDirectoryRow[];
  loading: boolean;
  /** Total matching rows, to flag when the marker fetch was truncated. */
  totalCount: number;
  geoCondition: GeoFilterCondition | null;
  /** False while the backend does not support geo filters — hides the tool. */
  radiusToolEnabled?: boolean;
  onApplyGeoFilter: (condition: GeoFilterCondition) => void;
  /** Debounced by the caller — safe for drags and radius typing. */
  onUpdateGeoFilter: (condition: GeoFilterCondition) => void;
  onClearGeoFilter: () => void;
  /** Increments when a chip asks to edit the radius filter on the map. */
  geoEditRequestToken?: number;
  onGeoEditRequestConsumed?: () => void;
  height?: number;
};

export default function SiteDirectoryMap({
  sites,
  loading,
  totalCount,
  geoCondition,
  radiusToolEnabled = true,
  onApplyGeoFilter,
  onUpdateGeoFilter,
  onClearGeoFilter,
  geoEditRequestToken,
  onGeoEditRequestConsumed,
  height = 620,
}: SiteDirectoryMapProps) {
  const { buildUrl } = useScopeParams();
  const navigate = useNavigate();
  const profileHidden = useFeatureFlag(FEATURE_FLAGS.SITE_PROFILE_LINKS_HIDDEN);
  const mapRef = useRef<L.Map | null>(null);

  const [placementMode, setPlacementMode] = useState(false);
  const [draft, setDraft] = useState<GeoFilterCondition | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [clickedGeo, setClickedGeo] = useState<ClickedGeo | null>(null);

  // The condition being visualized: an unapplied draft wins over the filter.
  const activeCondition = draft ?? geoCondition;
  const isDraft = draft != null;

  // A chip's "Edit on map" bumps the token; open the editor and focus the
  // circle, then hand the token back so a later manual visit doesn't reopen.
  useEffect(() => {
    if (!geoEditRequestToken) return;
    setEditorOpen(true);
    const condition = geoCondition;
    if (condition && mapRef.current) {
      mapRef.current.fitBounds(
        L.latLng(condition.lat, condition.lng).toBounds(
          condition.radius_km * 2000,
        ),
        { padding: [40, 40] },
      );
    }
    onGeoEditRequestConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs per request token
  }, [geoEditRequestToken]);

  const sitesWithCoords = useMemo(
    () => sites.filter((s) => s.latitude != null && s.longitude != null),
    [sites],
  );
  const missingCoordsCount = sites.length - sitesWithCoords.length;

  const placeCenter = (lat: number, lng: number) => {
    setPlacementMode(false);
    setEditorOpen(true);
    if (geoCondition) {
      // Filter already applied — move its center through the normal path.
      onApplyGeoFilter({ ...geoCondition, lat, lng, label: undefined });
      return;
    }
    setDraft(
      createGeoFilter({
        lat,
        lng,
        radiusKm: displayUnitToKm(DEFAULT_RADIUS_MILES, "mi"),
        displayUnit: DEFAULT_GEO_DISPLAY_UNIT,
      }),
    );
  };

  const handleConditionChange = (condition: GeoFilterCondition) => {
    if (isDraft) {
      setDraft(condition);
    } else {
      onUpdateGeoFilter(condition);
    }
  };

  const handlePinDragEnd = (lat: number, lng: number) => {
    if (!activeCondition) return;
    handleConditionChange({ ...activeCondition, lat, lng, label: undefined });
  };

  const handleApply = () => {
    if (!draft) return;
    onApplyGeoFilter(draft);
    setDraft(null);
  };

  const handleClose = () => {
    setDraft(null);
    setEditorOpen(false);
  };

  const handleRemove = () => {
    onClearGeoFilter();
    setDraft(null);
    setEditorOpen(false);
  };

  const markers = useMemo(
    () =>
      sitesWithCoords.map((site) => (
        <Marker
          key={site.site_key}
          position={[site.latitude!, site.longitude!]}
          icon={siteIcon}
          eventHandlers={
            placementMode
              ? {
                  // Snap-center the radius on this site.
                  click: () => placeCenter(site.latitude!, site.longitude!),
                }
              : undefined
          }
        >
          {!placementMode && (
            <Popup>
              <div className="min-w-[190px]">
                <div className="font-semibold text-[13px] text-[#1F2933] mb-0.5 leading-tight">
                  {site.site_name}
                </div>
                <div className="text-[11px] text-[#6B7280] mb-2">
                  {site.canonical_address || site.country}
                </div>
                <div className="flex gap-3 mb-3 text-[12px]">
                  <div>
                    <span className="font-semibold text-[#1F2933]">
                      {site.trial_count_portfolio ?? 0}
                    </span>{" "}
                    <span className="text-[#8C8C8C]">
                      trial{(site.trial_count_portfolio ?? 0) !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold text-[#1F2933]">
                      {site.total_enrolled ?? 0}
                    </span>{" "}
                    <span className="text-[#8C8C8C]">enrolled</span>
                  </div>
                </div>
                {!profileHidden && (
                  <button
                    type="button"
                    className="w-full text-[11px] font-medium text-white bg-[#2B4752] rounded px-2 py-1.5 hover:bg-[#3a5f6e]"
                    onClick={() =>
                      navigate(buildUrl(`site-profiles/${site.site_key}`))
                    }
                  >
                    View Site Profile →
                  </button>
                )}
              </div>
            </Popup>
          )}
        </Marker>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- placeCenter reads current props
    [sitesWithCoords, placementMode, buildUrl, navigate, profileHidden],
  );

  return (
    <div
      className="rounded-[8px] border border-[#F0F2F4]"
      style={{ boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }}
      data-testid="site-directory-map"
    >
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[#F0F2F4] bg-white rounded-t-[8px]">
        {radiusToolEnabled && (
          <button
            type="button"
            data-testid="radius-tool-toggle"
            className={`flex items-center gap-1.5 text-[12px] font-medium rounded px-2.5 py-1 border ${
              placementMode
                ? "bg-[#D2EFF9] border-[#06709B] text-[#06709B]"
                : "bg-white border-[#D9D9D9] text-[#2B4752] hover:bg-gray-50"
            }`}
            onClick={() => setPlacementMode((mode) => !mode)}
          >
            <AimOutlined />
            {placementMode ? "Click the map to set center" : "Radius filter"}
          </button>
        )}
        {placementMode && (
          <span className="text-[11px] text-[#8C8C8C]">
            Click a point or a site marker to center the radius
          </span>
        )}
        <div className="ml-auto flex items-center gap-3 text-[12px] text-[#8C8C8C] whitespace-nowrap">
          {missingCoordsCount > 0 && (
            <span data-testid="missing-coords-note">
              {missingCoordsCount} site{missingCoordsCount !== 1 ? "s" : ""}{" "}
              without coordinates{" "}
              {geoCondition
                ? "are excluded from radius results"
                : "not shown on the map"}
            </span>
          )}
          {totalCount > sites.length && (
            <span>
              showing first {sites.length.toLocaleString()} of{" "}
              {totalCount.toLocaleString()} sites
            </span>
          )}
          <span data-testid="map-site-count">
            {loading
              ? "Loading sites…"
              : `${sitesWithCoords.length} site${
                  sitesWithCoords.length !== 1 ? "s" : ""
                }`}
          </span>
        </div>
      </div>

      <div
        className="relative overflow-hidden rounded-b-[8px]"
        style={{ height, cursor: placementMode ? "crosshair" : undefined }}
      >
        <MapContainer
          ref={mapRef}
          center={[35, -20]}
          zoom={2.5}
          zoomSnap={0}
          scrollWheelZoom
          wheelDebounceTime={100}
          wheelPxPerZoomLevel={120}
          attributionControl={false}
          maxZoom={15}
          minZoom={1}
          zoomControl
          maxBounds={[
            [-90, -180],
            [90, 180],
          ]}
          className="w-full h-full bg-[#E3F1F4]"
        >
          <FitBoundsOnce sites={sitesWithCoords} />
          <PlacementClickHandler active={placementMode} onPlace={placeCenter} />
          <CountryClickHandler
            active={!placementMode}
            sites={sitesWithCoords}
            onLoading={setGeocoding}
            onGeoClick={setClickedGeo}
          />
          <TileLayer
            noWrap
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MarkerClusterGroup
            iconCreateFunction={createClusterIcon}
            showCoverageOnHover={false}
            removeOutsideVisibleBounds
            animate={false}
            chunkedLoading
            spiderfyOnMaxZoom={false}
          >
            {markers}
          </MarkerClusterGroup>

          {/* Country/region options popup — shown after an idle-mode click */}
          {clickedGeo && !placementMode && (
            <Popup
              position={[clickedGeo.lat, clickedGeo.lng]}
              eventHandlers={{ remove: () => setClickedGeo(null) }}
            >
              <div className="min-w-[200px]">
                <div className="text-[10px] font-semibold text-[#8C8C8C] uppercase tracking-wide mb-0.5">
                  Country
                </div>
                <div className="font-semibold text-[14px] text-[#1F2933] mb-1">
                  {clickedGeo.country}
                </div>
                <div className="flex gap-3 mb-3 text-[12px] text-[#4B5563]">
                  <span>
                    <strong className="text-[#1F2933]">
                      {clickedGeo.siteCount}
                    </strong>{" "}
                    site{clickedGeo.siteCount !== 1 ? "s" : ""}
                  </span>
                  <span>
                    <strong className="text-[#1F2933]">
                      {clickedGeo.totalEnrolled}
                    </strong>{" "}
                    enrolled
                  </span>
                </div>
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    className="w-full text-[11px] font-medium text-white bg-[#2B4752] rounded px-2 py-1.5 hover:bg-[#3a5f6e] text-left"
                    onClick={() =>
                      navigate(
                        buildUrl(
                          buildGeoProfileUrl("country", clickedGeo.country),
                        ),
                      )
                    }
                  >
                    View {clickedGeo.country} Profile →
                  </button>
                  {clickedGeo.regions.map((region) => (
                    <button
                      key={region}
                      type="button"
                      className="w-full text-[11px] font-medium text-[#2B4752] bg-[#E8EEF2] rounded px-2 py-1.5 hover:bg-[#d4dde3] text-left"
                      onClick={() =>
                        navigate(buildUrl(buildGeoProfileUrl("region", region)))
                      }
                    >
                      View {region} Region →
                    </button>
                  ))}
                </div>
              </div>
            </Popup>
          )}

          {activeCondition && (
            <>
              <Circle
                center={[activeCondition.lat, activeCondition.lng]}
                radius={activeCondition.radius_km * 1000}
                pathOptions={{
                  color: RADIUS_CIRCLE_COLOR,
                  weight: 1.5,
                  fillColor: RADIUS_CIRCLE_COLOR,
                  fillOpacity: 0.12,
                }}
              />
              <Marker
                position={[activeCondition.lat, activeCondition.lng]}
                icon={centerPinIcon}
                draggable
                zIndexOffset={1000}
                eventHandlers={{
                  click: () => setEditorOpen(true),
                  dragend: (e) => {
                    const { lat, lng } = (e.target as L.Marker).getLatLng();
                    handlePinDragEnd(lat, lng);
                  },
                }}
              />
            </>
          )}
        </MapContainer>

        {editorOpen && activeCondition && (
          <RadiusEditorPanel
            condition={activeCondition}
            isDraft={isDraft}
            onConditionChange={handleConditionChange}
            onApply={handleApply}
            onClose={handleClose}
            onRemove={handleRemove}
          />
        )}

        {(loading || geocoding) && (
          <div className="absolute bottom-3 left-3 z-[1000] flex items-center gap-1.5 bg-white/90 rounded px-2.5 py-1 text-[11px] text-[#4B5563] shadow backdrop-blur-sm">
            <span
              className="inline-block w-3 h-3 border-2 border-t-transparent rounded-full animate-spin"
              style={{
                borderColor: BRAND_COLOR,
                borderTopColor: "transparent",
              }}
            />
            {loading ? "Updating sites…" : "Looking up location…"}
          </div>
        )}

        {!placementMode && (
          <div className="absolute bottom-3 right-3 z-[1000] text-[10px] text-[#8C8C8C] bg-white/80 rounded px-1.5 py-0.5 backdrop-blur-sm">
            Click a country to view options
          </div>
        )}
      </div>
    </div>
  );
}
